import React, { useState } from 'react';
import { Form, Select, InputNumber, Row, Col, Slider, Typography, Divider } from 'antd';

const { Text } = Typography;

export interface AnimatedHeatmapConfig {
    resolution?: string;
    heatmapGridResolutionX?: number | null;
    heatmapGridResolutionY?: number | null;
    hexagonSizeMeters?: number | null;
    templateOpacity?: number | null;
    heatmapOpacity?: number | null;
    heatmapColorScheme?: string | null;
    invertColors?: boolean | null;
    heatmapValueRangeMode?: 'auto' | 'fixed' | 'areaDefault' | null;
    heatmapValueMin?: number | null;
    heatmapValueMax?: number | null;
    includeDescendants?: boolean;  // Whether to include descendant areas (default: false for heatmaps)
}

interface Props {
    initialConfig: AnimatedHeatmapConfig;
    onChange: (config: AnimatedHeatmapConfig) => void;
}

export const SectionConfigAnimatedHeatmap: React.FC<Props> = ({ initialConfig, onChange }) => {
    const [config, setConfig] = useState<AnimatedHeatmapConfig>({
        ...initialConfig,
        includeDescendants: initialConfig.includeDescendants ?? false  // Default: single area only
    });

    const updateConfig = (field: keyof AnimatedHeatmapConfig, value: any) => {
        const newConfig = { ...config, [field]: value };
        setConfig(newConfig);
        onChange(newConfig); // Call onChange directly to avoid infinite loop
    };

    return (
        <div>
            <Divider orientation="left">Time Settings</Divider>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="Time Resolution">
                        <Select
                            value={config.resolution || 'day'}
                            onChange={(val) => updateConfig('resolution', val)}
                            options={[
                                { label: 'Hour', value: 'hour' },
                                { label: 'Day', value: 'day' },
                                { label: 'Week', value: 'week' },
                                { label: 'Month', value: 'month' },
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left">Heatmap Visualization</Divider>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="Template Grid Resolution X">
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="e.g., 50"
                            value={config.heatmapGridResolutionX ?? undefined}
                            onChange={(val) => updateConfig('heatmapGridResolutionX', val)}
                            min={10}
                            max={200}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Template Grid Resolution Y">
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Auto (based on X and aspect ratio)"
                            value={config.heatmapGridResolutionY ?? undefined}
                            onChange={(val) => updateConfig('heatmapGridResolutionY', val)}
                            min={10}
                            max={200}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="GPS Hexagon Size (cm)">
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="50"
                            value={config.hexagonSizeMeters ?? 50}
                            onChange={(val) => updateConfig('hexagonSizeMeters', val)}
                            min={10}
                            max={500}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Color Scheme">
                        <Select
                            value={config.heatmapColorScheme || 'blue-red'}
                            onChange={(val) => updateConfig('heatmapColorScheme', val)}
                            options={[
                                { label: 'Blue → Red', value: 'blue-red' },
                                { label: 'Red → Green', value: 'green-red' },
                                { label: 'Red → Yellow → Green', value: 'red-yellow-green' },
                                { label: 'Yellow → Red', value: 'yellow-red' },
                                { label: 'Viridis', value: 'viridis' },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Invert Colors">
                        <Select
                            value={config.invertColors ? 'inverted' : 'normal'}
                            onChange={(val) => updateConfig('invertColors', val === 'inverted')}
                            options={[
                                { label: 'Normal', value: 'normal' },
                                { label: 'Inverted', value: 'inverted' },
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left">Opacity Controls</Divider>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label={`Template Opacity: ${config.templateOpacity ?? 70}%`}>
                        <Slider
                            min={0}
                            max={100}
                            value={config.templateOpacity ?? 70}
                            onChange={(val) => updateConfig('templateOpacity', val)}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label={`Heatmap Opacity: ${config.heatmapOpacity ?? 90}%`}>
                        <Slider
                            min={0}
                            max={100}
                            value={config.heatmapOpacity ?? 90}
                            onChange={(val) => updateConfig('heatmapOpacity', val)}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left">Value Range</Divider>
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item label="Value Range Mode">
                        <Select
                            value={config.heatmapValueRangeMode || 'auto'}
                            onChange={(val) => updateConfig('heatmapValueRangeMode', val)}
                            options={[
                                { label: 'Auto', value: 'auto' },
                                { label: 'Fixed', value: 'fixed' },
                                { label: 'Area default (metric color rule)', value: 'areaDefault' },
                            ]}
                        />
                    </Form.Item>
                </Col>
                {config.heatmapValueRangeMode === 'fixed' && (
                    <>
                        <Col span={8}>
                            <Form.Item label="Min Value">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={config.heatmapValueMin ?? undefined}
                                    onChange={(val) => updateConfig('heatmapValueMin', val)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Max Value">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={config.heatmapValueMax ?? undefined}
                                    onChange={(val) => updateConfig('heatmapValueMax', val)}
                                />
                            </Form.Item>
                        </Col>
                    </>
                )}
            </Row>
        </div>
    );
};

export default SectionConfigAnimatedHeatmap;
